import { useParams } from 'react-router-dom';
import { Stack, Text, Title, Group, Button, ScrollArea, Divider, useMantineTheme } from '@mantine/core';
import { useCallback, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { CenteredLoader } from '../../components';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import useEnsisMutation from '../../hooks/useEnsisMutation';
import DefaultOutlineBuilder from '../../components/DefaultOutlineBuilder/DefaultOutlineBuilder';
import { Plus } from '../../icons';
import SelectProposalSpacing from '../../components/SelectProposalSpacing';

export interface DefaultSection {
  title: string
  id?: string
  subsections?: DefaultSection[]
}

const stripIdsFromSections: (outline: DefaultSection[]) => DefaultSection[] = (outline) => {
  return outline.filter(section => section.title !== '').map(section => ({
    title: section.title,
    ...(section.subsections !== undefined && {
      subsections: section.subsections.filter(section => section.title !== '').map(sub => ({ title: sub.title }))
    })
  }));
};

// must add ids to sections to ensure there is a constant and unique id for each section for drag and drop
const addIdsToSections: (outline: DefaultSection[]) => DefaultSection[] = (outline) => {
  return outline.map(section => ({
    title: section.title,
    ...(section.subsections !== undefined && {
      subsections: section.subsections.map(sub => ({ title: sub.title, id: uuidv4() }))
    }),
    id: uuidv4()
  }));
};

const ProposalFormatSettings: React.FC = () => {
  const { organization } = useParams();

  const [selectedValue, setSelectedValue] = useState<number>(1);
  const [defaultOutline, setDefaultOutline] = useState<DefaultSection[]>([]);

  const { colors } = useMantineTheme();
  const { data, isLoading } = useEnsisQuery(
    `/app/organizations/${organization}/data`,
    {
      onSuccess: (data) => {
        setSelectedValue(data?.default_proposal_spacing ?? 1);
        // must add ids to sections to ensure there is a constant and unique id for each section for drag and drop
        setDefaultOutline(addIdsToSections(data?.default_proposal_outline_sections as DefaultSection[]));
      }
    }
  );

  const updateOrgMutation = useEnsisMutation(
    `/app/organizations/${organization}/data`,
    {
      successMessage: 'Settings Updated',
      requestType: 'patch',
      queryKeysToInvalidate: [`/app/organizations/${organization}/data`]
    }
  );

  const handleSubmit = useCallback(() => {
    updateOrgMutation.mutate({
      default_proposal_spacing: selectedValue,
      default_proposal_outline_sections: stripIdsFromSections(defaultOutline)
    }, {
      onSuccess: (data) => {
        setDefaultOutline(addIdsToSections(data.default_proposal_outline_sections as DefaultSection[]));
      }
    });
  }, [selectedValue, defaultOutline, updateOrgMutation]);

  const handleCancel = useCallback(() => {
    setSelectedValue(data?.default_proposal_spacing ?? 1);
    setDefaultOutline(addIdsToSections(data?.default_proposal_outline_sections as DefaultSection[]));
  }, [data]);

  const handleDeleteOutline = useCallback(() => {
    updateOrgMutation.mutate({
      default_proposal_outline_sections: []
    }, {
      onSuccess: () => {
        setDefaultOutline([]);
      }
    });
  }, [updateOrgMutation.mutate]);

  const handleAddSection = useCallback(() => {
    setDefaultOutline(prev => [...prev, { title: '', id: uuidv4() }]);
  }, []);

  const hasDefaultOutline = defaultOutline.length > 0;
  if (isLoading) {
    return <CenteredLoader />;
  }

  const spacingUnchanged = data?.default_proposal_spacing === selectedValue;
  const outlineUnchanged = JSON.stringify(
    data?.default_proposal_outline_sections
  ) === JSON.stringify(stripIdsFromSections(defaultOutline));
  const settingUnchanged = spacingUnchanged && outlineUnchanged;

  const createOutlineButton = (
    <Button
      onClick={handleAddSection}
      leftSection={<Plus />}
    >
      Create Outline
    </Button>
  );

  const deleteOutlineButton = (
    <Button onClick={handleDeleteOutline} variant='subtle' c='red'>
      Delete Outline
    </Button>
  );
  return (
    <Stack flex={1} gap={0}>
      <ScrollArea flex={1} mih={400}>
        <Stack gap={0}>
          <Title order={6} mb={12}>Default Spacing</Title>
          <Text fz='sm' c={colors.gray[7]} mb={24}>
            Set the default line spacing (e.g., single, 1.5, double) that will be applied to new proposals.
          </Text>
          <SelectProposalSpacing
            initialSpacing={data?.default_proposal_spacing ?? 1}
            onSubmit={setSelectedValue}
          />
          <Group justify='space-between' mt={16} mb={12}>
            <Title order={6}> Default Outline </Title>
            {defaultOutline.length > 0 ? deleteOutlineButton : createOutlineButton}
          </Group>
          <Text fz='sm' c={colors.gray[7]} mb={24}>
            {'Set your organization\'s default proposal outline. '}
            This outline applies to new proposals and provides a consistent starting point for your team.
          </Text>
          {hasDefaultOutline &&
            <DefaultOutlineBuilder defaultOutline={defaultOutline} setDefaultOutline={setDefaultOutline} />}
        </Stack>
      </ScrollArea>
      <Divider mb={8} />
      <Group>
        <Button
          disabled={settingUnchanged || updateOrgMutation?.isPending}
          onClick={handleCancel}
          variant='subtle'
        >
          Cancel
        </Button>
        <Button
          disabled={settingUnchanged || updateOrgMutation?.isPending}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </Group>
    </Stack>
  );
};

export default ProposalFormatSettings;
